import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  // Talio: desktop-only zoom in the existing product photograph. The mobile
  // full-screen viewer and its touch gestures remain Horizon's original code.
  #inlineZoom = null;

  #handleInlinePointerMove = (event) => {
    if (!this.#inlineZoom || event.pointerType === 'touch') return;
    this.#moveInlineZoom(event.clientX, event.clientY);
  };

  #handleInlineKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#inlineZoom) return;
    event.preventDefault();
    this.#endInlineZoom();
  };

  #handleInlineGalleryClick = (event) => {
    if (!this.#inlineZoom) return;
    const target = event.target;
    // Gallery thumbnails and arrow buttons should restore the unzoomed view.
    if (target instanceof Node && !this.#inlineZoom.source.contains(target)) {
      this.#endInlineZoom();
    }
  };

  #handleInlineResize = () => {
    if (this.#inlineZoom) this.#endInlineZoom();
  };

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.closest('media-gallery')?.addEventListener('click', this.#handleInlineGalleryClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.closest('media-gallery')?.removeEventListener('click', this.#handleInlineGalleryClick, true);
    this.#endInlineZoom();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    // Only desktop mouse/pen interactions with image slides use Talio's
    // in-place magnifier. Everything else preserves Horizon's native viewer.
    const source = event.target instanceof Element
      ? event.target.closest('slideshow-slide.product-media-container--image, li.product-media-container--image')
      : null;
    const image = source?.querySelector('.product-media__image');
    const frame = image?.closest('.product-media');
    if (window.matchMedia('(min-width: 750px)').matches &&
        event.pointerType !== 'touch' && source && image instanceof HTMLImageElement && frame) {
      event.preventDefault();
      this.#toggleInlineZoom(source, image, frame, event);
      return;
    }

    this.#endInlineZoom();
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Toggle an in-place 2.5x zoom, focussed on the point clicked. No dialog
   * opens and the product gallery retains its normal dimensions and controls.
   */
  #toggleInlineZoom(source, image, frame, event) {
    if (this.#inlineZoom?.source === source) {
      this.#endInlineZoom();
      return;
    }
    this.#endInlineZoom();

    const layer = document.createElement('div');
    layer.className = 'talio-inline-zoom-layer';
    layer.setAttribute('aria-hidden', 'true');

    const magnified = document.createElement('img');
    magnified.alt = '';
    magnified.draggable = false;
    magnified.decoding = 'async';
    magnified.src = image.currentSrc || image.src;
    // The full-size image must use the same fit and focal point as the gallery.
    const style = getComputedStyle(image);
    magnified.style.objectFit = style.objectFit;
    magnified.style.objectPosition = style.objectPosition;
    layer.append(magnified);
    frame.append(layer);
    source.classList.add('talio-inline-zoom-active');

    this.#inlineZoom = { source, layer, magnified };
    source.addEventListener('pointermove', this.#handleInlinePointerMove);
    document.addEventListener('keydown', this.#handleInlineKeydown);
    window.addEventListener('resize', this.#handleInlineResize);

    const bounds = layer.getBoundingClientRect();
    const centerClick = event.detail === 0 || !('clientX' in event);
    this.#moveInlineZoom(
      centerClick ? bounds.left + bounds.width / 2 : event.clientX,
      centerClick ? bounds.top + bounds.height / 2 : event.clientY
    );

    // Swap in Shopify's high-resolution image after it's loaded, without a
    // white flash or disturbing the photograph currently being inspected.
    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (highResolutionUrl && highResolutionUrl !== magnified.src) {
      const highRes = new Image();
      highRes.onload = () => {
        if (this.#inlineZoom?.magnified === magnified) magnified.src = highResolutionUrl;
      };
      highRes.src = highResolutionUrl;
    }
  }

  /** Re-centre the magnified detail based on pointer position. */
  #moveInlineZoom(clientX, clientY) {
    const active = this.#inlineZoom;
    if (!active) return;
    const bounds = active.layer.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;

    const scale = 2.5;
    const x = Math.max(0, Math.min(1, (clientX - bounds.left) / bounds.width));
    const y = Math.max(0, Math.min(1, (clientY - bounds.top) / bounds.height));
    // Translate so the clicked detail is centred. Clamp at the image edges.
    const tx = Math.max(bounds.width * (1 - scale), Math.min(0, bounds.width * (0.5 - x * scale)));
    const ty = Math.max(bounds.height * (1 - scale), Math.min(0, bounds.height * (0.5 - y * scale)));
    active.magnified.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
  }

  /** Restore native gallery view and release listeners. */
  #endInlineZoom() {
    const active = this.#inlineZoom;
    if (!active) return;
    active.source.removeEventListener('pointermove', this.#handleInlinePointerMove);
    active.source.classList.remove('talio-inline-zoom-active');
    active.layer.remove();
    document.removeEventListener('keydown', this.#handleInlineKeydown);
    window.removeEventListener('resize', this.#handleInlineResize);
    this.#inlineZoom = null;
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
